<template>
  <div class="page-user-notifications">
    <BaseLoadingBar is-small v-if="isLoading" />
    <div class="page-user-notifications__card">
      <section class="page-user-notifications__column is-wide">
        <div class="page-user-notifications__header">
          <h2 class="page-user-notifications__title">{{ $t('page.admin-user-notifications.notify-me-title') }}</h2>
          <p class="page-user-notifications__subtitle">{{ $t('page.admin-user-notifications.notify-me-subtitle') }}</p>
        </div>

        <div class="page-user-notifications__rows">
          <template v-for="group in settingGroups">
            <h3 v-if="group.label" :key="`${group.key}-heading`" class="page-user-notifications__group">
              {{ group.label }}
            </h3>
            <template v-for="row in group.rows">
              <div :key="`${row.slug}-label`" class="page-user-notifications__row-text">
                <span class="page-user-notifications__row-label">{{ row.label }}</span>
                <span v-if="row.subtitle" class="page-user-notifications__row-subtitle">{{ row.subtitle }}</span>
              </div>
              <FormCheckbox
                v-for="flag in channels"
                :key="`${row.slug}-${flag}`"
                class="page-user-notifications__channel"
                :class="{ 'is-locked-off': isChannelLockedOff(row, flag) }"
                :name="`${row.slug}-${flag}`"
                :value="channelValue(row, flag)"
                :disabled="isChannelDisabled(row, flag)"
                :tooltipContent="channelTooltip(row, flag)"
                :label="$t(channelLabelKeys[flag])"
                @input="(checked) => onSettingToggle(row, flag, checked)"
              />
            </template>
          </template>
        </div>
      </section>

      <section class="page-user-notifications__column">
        <div class="page-user-notifications__header">
          <h2 class="page-user-notifications__title">{{ $t('page.admin-user-notifications.market-summary') }}</h2>
          <p class="page-user-notifications__subtitle">
            {{ $t('page.admin-user-notifications.market-summary-subtitle') }}
          </p>
        </div>

        <div class="page-user-notifications__options">
          <FormCheckbox
            v-for="row in marketSummaryRows"
            :key="row.section"
            :name="row.section"
            :value="marketSummaryForm[row.section]"
            :disabled="isReadOnly || row.isGated || isMarketSummarySaving"
            :tooltipContent="row.tooltip"
            :label="row.label"
            :sublabel="row.subtitle"
            @input="(checked) => onMarketSummaryToggle(row.section, checked)"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import { MarketSummarySections, NotificationSlugs } from '@/util/constants/notificationSlugs';
import { tenantHasBusinessIntelligenceAccess, tenantHasOpportunitiesAccess } from '@/util/module-access-util';
import { isTenantUpsellRestricted } from '@/util/upsell-tenant-restriction-util';

const I18N = 'page.admin-user-notifications';

// Rendering order of the channel checkboxes on each row.
const CHANNELS = ['isInAppSubscribed', 'isEmailSubscribed'];
const CHANNEL_LABEL_KEYS = {
  isInAppSubscribed: `${I18N}.channel-in-app`,
  isEmailSubscribed: `${I18N}.channel-email`,
};

// A row with a `group` sits under that heading, after a divider, at the end of the list; the others come first,
// flat. `slug` is the row's key and its notification type; a row that groups several types lists them all in `slugs`
// (the backend keeps one setting per type, the page one checkbox per situation). `locks` are channel flags the
// page forces: shown as-is, sent along when the rows are created, never toggled. In-app is mandatory for the
// invitation and assignation types; email is off for the types the Aviseur digest already covers. `gate` names the tenant
// module a row needs; `hideWhenUpsellRestricted` hides it for restricted tenants.
const SETTING_GROUPS = ['go-nogo'];
const SETTING_ROWS = [
  {
    slug: NotificationSlugs.OPPORTUNITY_VOTE_INVITATION,
    group: 'go-nogo',
    labelKey: `${I18N}.rows.vote-invitation`,
    subtitleKey: `${I18N}.rows.vote-invitation-subtitle`,
    locks: { isInAppSubscribed: true },
    gate: 'opportunities',
  },
  // The go/no-go's vote events, grouped by who hears of what: the inviter of each answer to their invitation,
  // the voter of their own vote, the owner and assignees when every vote is in, everyone involved of the final
  // decision and its changes. Both channels are the user's to choose.
  {
    slug: NotificationSlugs.OPPORTUNITY_VOTE_CAST,
    slugs: [
      NotificationSlugs.OPPORTUNITY_VOTE_CAST,
      NotificationSlugs.OPPORTUNITY_VOTE_CHANGED,
      NotificationSlugs.OPPORTUNITY_VOTE_WITHDRAWN,
    ],
    group: 'go-nogo',
    labelKey: `${I18N}.rows.vote-answered`,
    subtitleKey: `${I18N}.rows.vote-answered-subtitle`,
    locks: {},
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.OPPORTUNITY_VOTE_CONFIRMED,
    group: 'go-nogo',
    labelKey: `${I18N}.rows.vote-confirmed`,
    subtitleKey: `${I18N}.rows.vote-confirmed-subtitle`,
    locks: {},
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.OPPORTUNITY_VOTE_COMPLETED,
    group: 'go-nogo',
    labelKey: `${I18N}.rows.vote-completed`,
    subtitleKey: `${I18N}.rows.vote-completed-subtitle`,
    locks: {},
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.OPPORTUNITY_VOTE_DECIDED,
    slugs: [
      NotificationSlugs.OPPORTUNITY_VOTE_DECIDED,
      NotificationSlugs.OPPORTUNITY_VOTE_REDECIDED,
      NotificationSlugs.OPPORTUNITY_VOTE_REOPENED,
    ],
    group: 'go-nogo',
    labelKey: `${I18N}.rows.vote-decided`,
    subtitleKey: `${I18N}.rows.vote-decided-subtitle`,
    locks: {},
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.OPPORTUNITY_ASSIGNATIONS,
    labelKey: `${I18N}.rows.opportunity-assignation`,
    subtitleKey: `${I18N}.rows.opportunity-assignation-subtitle`,
    locks: { isInAppSubscribed: true },
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.TENDER_ASSIGNATIONS,
    labelKey: `${I18N}.rows.tender-assignation`,
    subtitleKey: `${I18N}.rows.tender-assignation-subtitle`,
    locks: { isInAppSubscribed: true },
  },
  {
    slug: NotificationSlugs.TENDER_OPPORTUNITIES_UPDATE,
    labelKey: `${I18N}.rows.tender-update`,
    subtitleKey: `${I18N}.tender-change-subtitle`,
    locks: { isEmailSubscribed: false },
    gate: 'opportunities',
  },
  {
    slug: NotificationSlugs.FOLLOWED_COMPETITOR_TENDER_UPDATED,
    labelKey: `${I18N}.competitor-activity`,
    subtitleKey: `${I18N}.competitor-activity-subtitle`,
    locks: { isEmailSubscribed: false },
    gate: 'businessIntelligence',
    hideWhenUpsellRestricted: true,
  },
];

// Each Aviseur checkbox is a section of the daily digest, not a notification setting row.
const MARKET_SUMMARY_ROWS = [
  {
    section: MarketSummarySections.NEW_TENDERS,
    labelKey: `${I18N}.new-tenders`,
    subtitleKey: `${I18N}.new-tenders-subtitle`,
  },
  {
    section: MarketSummarySections.CHANGED_TENDERS,
    labelKey: `${I18N}.tender-change`,
    subtitleKey: `${I18N}.tender-change-subtitle`,
    gate: 'opportunities',
  },
  {
    section: MarketSummarySections.COMPETITOR_TENDERS,
    labelKey: `${I18N}.competitor-activity`,
    subtitleKey: `${I18N}.competitor-activity-subtitle`,
    gate: 'businessIntelligence',
    hideWhenUpsellRestricted: true,
  },
];
const MARKET_SUMMARY_ORDER = Object.values(MarketSummarySections);

// Every notification type a row controls.
const rowSlugs = (row) => row.slugs || [row.slug];

export default {
  name: 'pageAdminUserNotifications',
  components: { BaseLoadingBar, FormCheckbox },
  data() {
    return {
      isLoading: true,
      hasLoadError: false,
      // row slug -> { isInAppSubscribed, isEmailSubscribed }: what the checkboxes show.
      settingsForm: {},
      // Rows (by slug) with a write in flight; both of their checkboxes are disabled meanwhile.
      savingSlugs: [],
      marketSummaryForm: {},
      isMarketSummarySaving: false,
    };
  },
  computed: {
    ...mapGetters('userNotificationSettings', {
      userNotificationSettings: 'userNotificationSettings',
      marketSummarySections: 'marketSummarySections',
      settingsRejected: 'rejected',
      marketSummaryRejected: 'marketSummaryRejected',
    }),
    ...mapGetters('notificationTypes', {
      notificationTypes: 'notificationTypes',
      notificationTypesRejected: 'rejected',
    }),
    ...mapGetters('auth', ['currentUser']),
    channels: () => CHANNELS,
    channelLabelKeys: () => CHANNEL_LABEL_KEYS,
    tenantIsRestricted() {
      return isTenantUpsellRestricted(this.currentUser && this.currentUser.tenantId);
    },
    isReadOnly() {
      return this.isLoading || this.hasLoadError;
    },
    gates() {
      return {
        opportunities: tenantHasOpportunitiesAccess(),
        businessIntelligence: tenantHasBusinessIntelligenceAccess(),
      };
    },
    settingRows() {
      return this.visibleRows(SETTING_ROWS).map((row) => ({
        ...this.describeRow(row),
        hasType: rowSlugs(row).every((slug) => this.notificationTypeBySlug(slug)),
        isSaving: this.savingSlugs.includes(row.slug),
      }));
    },
    settingGroups() {
      const ungrouped = { key: 'ungrouped', label: null, rows: this.settingRows.filter((row) => !row.group) };
      const groups = SETTING_GROUPS.map((key) => ({
        key,
        label: this.$t(`${I18N}.groups.${key}`),
        rows: this.settingRows.filter((row) => row.group === key),
      }));
      return [ungrouped, ...groups].filter((group) => group.rows.length > 0);
    },
    marketSummaryRows() {
      return this.visibleRows(MARKET_SUMMARY_ROWS).map((row) => this.describeRow(row));
    },
  },
  methods: {
    ...mapActions('userNotificationSettings', [
      'getUserNotificationSettings',
      'addUserNotificationSetting',
      'addUserNotificationSettings',
      'editUserNotificationSetting',
      'getMarketSummarySections',
      'updateMarketSummarySections',
    ]),
    ...mapActions('notificationTypes', ['getNotificationTypes']),
    visibleRows(rows) {
      return rows.filter((row) => !(row.hideWhenUpsellRestricted && this.tenantIsRestricted));
    },
    describeRow(row) {
      const isGated = row.gate ? !this.gates[row.gate] : false;
      return {
        ...row,
        label: this.$t(row.labelKey),
        subtitle: row.subtitleKey ? this.$t(row.subtitleKey) : null,
        isGated,
        tooltip: isGated ? this.$t('subscriptions.message') : null,
      };
    },
    settingBySlug(slug) {
      return (this.userNotificationSettings || []).find((setting) => setting.notificationType.slug === slug);
    },
    notificationTypeBySlug(slug) {
      return (this.notificationTypes || []).find((type) => type.slug === slug);
    },
    isChannelLocked(row, flag) {
      return flag in row.locks;
    },
    isChannelLockedOff(row, flag) {
      return this.isChannelLocked(row, flag) && !row.locks[flag];
    },
    channelTooltip(row, flag) {
      if (this.isChannelLockedOff(row, flag)) {
        return this.$t(`${I18N}.email-unsupported-tooltip`);
      }
      if (row.isGated && !this.isChannelLocked(row, flag)) return row.tooltip;
      return null;
    },
    channelValue(row, flag) {
      // A locked flag shows its forced value from the first paint, before the settings have loaded.
      if (this.isChannelLocked(row, flag)) return row.locks[flag];
      const channels = this.settingsForm[row.slug];
      return channels ? channels[flag] : false;
    },
    // The flag as stored for the row: on when every one of its types has it on. A type without a row is
    // subscribed to both channels. A locked flag shows its forced value whatever the stored rows say; nothing is
    // written to "fix" it.
    storedChannelValue(row, flag) {
      if (this.isChannelLocked(row, flag)) return row.locks[flag];
      return rowSlugs(row).every((slug) => {
        const setting = this.settingBySlug(slug);
        return setting ? setting[flag] : true;
      });
    },
    isChannelDisabled(row, flag) {
      return this.isReadOnly || this.isChannelLocked(row, flag) || row.isGated || !row.hasType || row.isSaving;
    },
    hydrateSettingsForm() {
      const form = {};
      SETTING_ROWS.forEach((row) => {
        form[row.slug] = {};
        CHANNELS.forEach((flag) => {
          form[row.slug][flag] = this.storedChannelValue(row, flag);
        });
      });
      this.settingsForm = form;
    },
    hydrateMarketSummaryForm() {
      const form = {};
      MARKET_SUMMARY_ORDER.forEach((section) => {
        form[section] = this.marketSummarySections.includes(section);
      });
      this.marketSummaryForm = form;
    },
    onSettingToggle(row, flag, checked) {
      const { slug } = row;
      const slugs = rowSlugs(row);
      if (!slugs.every((each) => this.notificationTypeBySlug(each)) || this.savingSlugs.includes(slug)) {
        return undefined;
      }

      this.settingsForm[slug][flag] = checked;
      this.savingSlugs = [...this.savingSlugs, slug];

      // One write per type the row controls: PUT changes the toggled flag only on the types with a stored row;
      // the others are created together, with the row's locked flag persisted so the stored rows match what
      // the user sees.
      const stored = slugs.map((each) => this.settingBySlug(each)).filter(Boolean);
      const missingTypeIds = slugs
        .filter((each) => !this.settingBySlug(each))
        .map((each) => this.notificationTypeBySlug(each).id);
      const writes = stored.map((setting) =>
        this.editUserNotificationSetting({ userNotificationSettingId: setting.id, [flag]: checked }),
      );
      if (missingTypeIds.length === 1) {
        writes.push(
          this.addUserNotificationSetting({ notificationTypeId: missingTypeIds[0], ...row.locks, [flag]: checked }),
        );
      } else if (missingTypeIds.length > 1) {
        writes.push(
          this.addUserNotificationSettings({ notificationTypeIds: missingTypeIds, ...row.locks, [flag]: checked }),
        );
      }

      // Every write settles before the row is released, and the row then shows what is stored: the writes that
      // went through are in the store, a failed one left its row as it was, and a batch returns a row another
      // session created meanwhile as it is, not as asked.
      return Promise.allSettled(writes)
        .then((results) => {
          CHANNELS.forEach((channel) => {
            this.settingsForm[slug][channel] = this.storedChannelValue(row, channel);
          });
          const failure = results.find((result) => result.status === 'rejected' && !axios.isCancel(result.reason));
          if (failure) this.$toast.error(failure.reason);
        })
        .finally(() => {
          this.savingSlugs = this.savingSlugs.filter((saving) => saving !== slug);
        });
    },
    onMarketSummaryToggle(section, checked) {
      if (this.isMarketSummarySaving) return undefined;

      const previous = { ...this.marketSummaryForm };
      this.marketSummaryForm = { ...previous, [section]: checked };
      // The PUT replaces the whole set, so the body is always the full selection in the API's order. The three
      // boxes stay disabled until it resolves: two concurrent replacements would clobber each other.
      const sections = MARKET_SUMMARY_ORDER.filter((key) => this.marketSummaryForm[key]);
      this.isMarketSummarySaving = true;

      return this.updateMarketSummarySections(sections)
        .catch((error) => {
          if (axios.isCancel(error)) return;
          this.marketSummaryForm = previous;
          this.$toast.error(error);
        })
        .finally(() => {
          this.isMarketSummarySaving = false;
        });
    },
  },
  async created() {
    await Promise.all([
      this.getUserNotificationSettings(),
      this.getNotificationTypes(),
      this.getMarketSummarySections(),
    ]);
    this.hasLoadError = Boolean(this.settingsRejected || this.notificationTypesRejected || this.marketSummaryRejected);
    if (this.hasLoadError) {
      // Writing against unknown stored rows would only produce more errors, so the card stays read-only.
      this.$toast.error(this.$t('toast.error'));
    } else {
      this.hydrateSettingsForm();
      this.hydrateMarketSummaryForm();
    }
    this.isLoading = false;
  },
};
</script>

<style lang="scss">
.page-user-notifications {
  padding: $space-xl;

  &__card {
    display: flex;
    padding: $space-lg;
    background: $color-white;
    border-radius: $border-radius-root;
    box-shadow: $shadow-default;
  }

  &__column {
    display: flex;
    flex: 2 1 0;
    flex-direction: column;
    gap: $space-md;
    min-width: 0;

    &.is-wide {
      flex-grow: 3;
    }

    & + & {
      margin-left: $space-lg;
      padding-left: $space-lg;
      border-left: 1px solid $color-border;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &__title {
    margin: 0;
    color: $color-normal;
    font-size: $font-size-xbigger;
    font-weight: $font-weight-semibold;
  }

  &__subtitle {
    margin: 0;
    color: $color-normal-pale;
    font-size: $font-size-small;
  }

  &__rows {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    column-gap: $space-sm;
    row-gap: $space-sm;
  }

  // A group heading; the line above it divides the group from the rows before it.
  &__group {
    grid-column: 1 / -1;
    margin: $space-sm 0 0;
    padding-top: $space-md;
    border-top: 1px solid $color-border;
    color: $color-normal-pale;
    font-size: $font-size-xsmall;
    font-weight: $font-weight-semibold;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &__row-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__row-label {
    color: $color-normal;
    font-size: $font-size-small;
  }

  &__row-subtitle {
    color: $color-normal-pale;
    font-size: $font-size-xsmall;
  }

  &__channel.form-checkbox {
    margin-right: 0;
  }

  &__channel.is-locked-off {
    cursor: help;

    .label__text {
      color: $color-text-disabled;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;

    .form-checkbox__label {
      align-items: flex-start;
    }

    .label__subtext {
      font-size: $font-size-xsmall;
    }
  }

  @media screen and (max-width: 1500px) {
    &__card {
      flex-direction: column;
    }

    &__column + &__column {
      margin-top: $space-lg;
      margin-left: 0;
      padding-top: $space-lg;
      padding-left: 0;
      border-top: 1px solid $color-border;
      border-left: none;
    }
  }

  // Phones: an event's name across the line, its two channels under it; beside them, the name had
  // a word a line.
  @include screen-below('tablet') {
    &__card {
      padding: $space-md;
    }

    &__rows {
      grid-template-columns: auto minmax(0, 1fr);
      column-gap: $space-lg;
      row-gap: $space-1;
    }

    &__row-text {
      grid-column: 1 / -1;
      padding-top: $space-3;

      &:first-child {
        padding-top: 0;
      }
    }
  }
}
</style>
